import {useAntdTable as useFormTable, useRequest} from "ahooks";
import request from "../utils/request";
import {Method} from "axios";
import {BaseOptions, OptionsWithFormat, PaginatedParams} from "ahooks/lib/useAntdTable";
import {Form} from "antd";
import {useState} from "react";
import {FormInstance} from "antd/lib/form";

interface Options<R, Item, U> extends OptionsWithFormat<R, Item, U> {
    method?: Method,
    selectable?: boolean
    downloadUrl?: string
    onSave?: (rowKey: any, record: any) => Function | Promise<any>
    onEditChange?: (dataIndex: string, value: any, form: FormInstance, record: any) => void
    extraParams?: { [key: string]: any }
}

interface BasicOptions<U> extends BaseOptions<U> {
    method?: Method,
    selectable?: boolean,
    downloadUrl?: string,
    onSave?: (rowKey: any, record: any) => Function | Promise<any>
    onEditChange?: (dataIndex: string, value: any, form: FormInstance, record: any) => void
    extraParams?: { [key: string]: any }
}

function useAntTable<R = any, Item = any, U extends Item = any>(url: string | undefined, options: Options<R, Item, U> | BasicOptions<U>) {

    const {method = 'get', selectable, downloadUrl, onSave, extraParams = {}} = options

    function service({current, pageSize, sorter}: PaginatedParams[0], formData: any) {
        formData = form.getFieldsValue()
        if (url) {
            setQueryParams(formData)
            const requestParamsKeys = Object.keys(formData).filter(i => formData[i] !== null && formData[i] !== undefined)
            const requestParams: { [key: string]: any } = {}
            requestParamsKeys.forEach(i => requestParams[i] = formData[i])
            return request.request<BaseResponse<Item>>({
                url: url,
                method: method,
                [method === 'get' ? 'params' : 'data']: {
                    page: current,
                    limit: pageSize,
                    ...requestParams,
                    ...extraParams
                }
            }).then(data => {
                setSelectedRowKeys([])
                setSelectedRows([])
                setEditingKey(undefined)
                setEditRecord(undefined)
                return {
                    list: data.data.data,
                    total: data.data.total
                }
            })
        } else {
            return Promise.resolve()
        }
    }

    function onDownload() {
        if (!downloadUrl) return undefined
        return request.request({
            url: downloadUrl,
            method: method,
            responseType: 'blob',
            [method === 'get' ? 'params' : 'data']: {
                ...queryParams,
                selected: selectedRowKeys.length > 0 ? (method === 'get' ? selectedRowKeys.join(',') : selectedRowKeys) : null,
                fields: columns
            },
        }).then(response => {
            let fileName = response.headers['content-disposition'].split("''")[1]
            const link = document.createElement('a')
            link.href = URL.createObjectURL(response.data)
            link.download = decodeURIComponent(fileName)
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            link.remove()
        })
    }

    function onSelectedChange(keys: any[], rows: any[]) {
        setSelectedRowKeys(keys)
        setSelectedRows(rows)
    }

    function onEdit(rowKey: any, record: any) {
        setEditingKey(rowKey)
        setEditRecord(record)
        editForm.setFieldsValue(record)
    }

    function onEditCancel() {
        setEditingKey(undefined)
        setEditRecord(undefined)
    }

    async function onEditSave() {
        const values = await editForm.validateFields()
        await onSave?.(editingKey, {...editRecord, ...values})
        setEditingKey(undefined)
        setEditRecord(undefined)
    }

    function onEditChange(dataIndex: string, value: any) {
        options.onEditChange?.(dataIndex, value, editForm, editRecord)
    }

    function onColumnsChange(columns: any[]) {
        const allFields = columns.filter(item => item.checked).map(item => item.dataIndex).join(",")
        setColumns(allFields)
    }

    const [form] = Form.useForm()
    const [editForm] = Form.useForm()
    const {run: download, loading: downloading} = useRequest(onDownload, {manual: true})
    const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([])
    const [selectedRows, setSelectedRows] = useState<any[]>([])
    const [queryParams, setQueryParams] = useState<any>({})
    const [editingKey, setEditingKey] = useState()
    const [editRecord, setEditRecord] = useState<any>()
    const [columns, setColumns] = useState<string>('')
    const rowSelection = {
        fixed: true,
        selectedRowKeys,
        onChange: onSelectedChange
    }

    const {tableProps: tp, search, pagination, ...others} = useFormTable(service, {...options, form})
    pagination.showQuickJumper = true
    pagination.showSizeChanger = true
    pagination.pageSizeOptions = [10, 50, 100, 200, 500]

    const tableProps = {
        ...tp,
        pagination: pagination as any,
        submit: search.submit,
        form: form,
        editForm: editForm,
        editingKey: editingKey,
        rowSelection: selectable ? rowSelection : undefined,
        onColumnsChange: onColumnsChange,
        onEditChange: onEditChange,
        onEditClick: onEdit,
    }
    return {
        tableProps,
        search,
        download,
        downloading,
        selectedRowKeys,
        selectedRows,
        editingKey,
        edit: onEdit,
        saveEdit: onEditSave,
        cancelEdit: onEditCancel, ...others
    }
}

export default useAntTable
